/**
 * useAutoGroupLights Hook
 *
 * 조명(l-light / l-lightingline) 셀들을 자동으로 클러스터링한다.
 * shift+drag + shift+G 로 수작업하던 것을 한 번에 처리.
 *
 * 동작:
 *  1. 다음 두 종류를 모두 후보로 수집한다.
 *     - "orphan": layer ∈ {l-light, l-lightingline} 이면서 isGroup !== true 인 단일 셀
 *     - "existing group": isGroup === true 이고 groupName === 'l-light' (또는 layer === 'l-light')
 *       인 기존 라이트 프레임 그룹. 임계값을 키웠을 때 주변 orphan/다른 그룹을 흡수할 수
 *       있어야 하기 때문에 후보에 포함시킨다.
 *  2. 각 셀의 실제 bbox 계산 (vertices 우선, fallback position+size).
 *     - 기존 그룹은 groupElements 에서 vertices 로 bbox 4점을 저장하므로 그대로 동작.
 *  3. 두 bbox 의 AABB 거리가 임계값 이하 (또는 겹침) 이면 같은 클러스터로 union-find
 *  4. 크기 ≥ 2 인 클러스터만 반환 — 단독 조명/단독 그룹은 의미 없음
 *
 * 실제 그룹 생성/병합은 호출자(EditorPage) 가 useGrouping 의 groupElements,
 * addToGroup, ungroupElements 를 조합해 수행한다. 본 훅은 후보 수집·클러스터링·
 * 임계값 추천만 담당해 순수 계산 유지.
 */

import { useCallback } from 'react'
import { dia } from '@joint/core'
import { snapshotVertices } from '../lib/lightFrameGeometry'

/** 조명 후보 셀 - id 와 bbox + 기존 그룹 여부 플래그. */
export interface AutoGroupCandidate {
  id: string
  bbox: { x: number; y: number; width: number; height: number }
  /** true 면 이미 isGroup=true 인 라이트 프레임. confirm 단계에서 addToGroup/merge 분기에 사용. */
  isExistingGroup: boolean
}

export interface AutoGroupPreview {
  /** 후보 조명 셀 전체 (orphan + 기존 라이트 그룹). */
  candidates: AutoGroupCandidate[]
  /** 추천 임계값 — 후보 bbox 짧은 변의 중앙값 × 0.5. */
  suggestedThreshold: number
  /** suggestedThreshold 산출 시 사용한 median short side. UI 슬라이더 범위 결정용. */
  medianShortSide: number
}

export interface UseAutoGroupLightsReturn {
  /** 현재 그래프에서 후보 셀과 추천 임계값을 한 번 계산. */
  buildPreview: () => AutoGroupPreview
  /** 주어진 임계값으로 후보를 클러스터링. 각 클러스터는 element id 배열, 크기 ≥ 2. */
  clusterByThreshold: (
    candidates: AutoGroupCandidate[],
    threshold: number,
  ) => string[][]
}

/** 라이트 layer 후보 — 신규 canonical + legacy 둘 다 포함. */
const LIGHT_LAYERS: ReadonlySet<string> = new Set(['l-light', 'l-lightingline'])

/** vertices 있으면 그것의 AABB, 없으면 position()+size() 로 bbox 산출. */
function computeBbox(el: dia.Element): {
  x: number
  y: number
  width: number
  height: number
} {
  const data = (el.get('data') ?? {}) as {
    vertices?: Array<[number, number]>
  }
  const vertices = data.vertices?.length ? snapshotVertices(el.toJSON()) : undefined
  if (vertices && vertices.length > 0) {
    let minX = Infinity
    let minY = Infinity
    let maxX = -Infinity
    let maxY = -Infinity
    for (const [x, y] of vertices) {
      if (x < minX) minX = x
      if (x > maxX) maxX = x
      if (y < minY) minY = y
      if (y > maxY) maxY = y
    }
    return { x: minX, y: minY, width: maxX - minX, height: maxY - minY }
  }
  const pos = el.position()
  const size = el.size()
  return { x: pos.x, y: pos.y, width: size.width, height: size.height }
}

/** 두 AABB 간 최소 거리. 겹치면 0. */
function rectDistance(
  a: { x: number; y: number; width: number; height: number },
  b: { x: number; y: number; width: number; height: number },
): number {
  const dx = Math.max(0, Math.max(a.x - (b.x + b.width), b.x - (a.x + a.width)))
  const dy = Math.max(
    0,
    Math.max(a.y - (b.y + b.height), b.y - (a.y + a.height)),
  )
  return Math.sqrt(dx * dx + dy * dy)
}

/** Simple Union-Find with path compression. */
class UnionFind {
  private parent: number[]

  constructor(n: number) {
    this.parent = Array.from({ length: n }, (_, i) => i)
  }

  find(i: number): number {
    let root = i
    while (this.parent[root] !== root) root = this.parent[root]
    // path compression
    let cur = i
    while (this.parent[cur] !== root) {
      const next = this.parent[cur]
      this.parent[cur] = root
      cur = next
    }
    return root
  }

  union(a: number, b: number): void {
    const ra = this.find(a)
    const rb = this.find(b)
    if (ra !== rb) this.parent[ra] = rb
  }
}

/** 정렬된 배열에서 중앙값 반환. 비어있으면 0. */
function median(values: number[]): number {
  if (values.length === 0) return 0
  const sorted = [...values].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  if (sorted.length % 2 === 0) return (sorted[mid - 1] + sorted[mid]) / 2
  return sorted[mid]
}

export function useAutoGroupLights(
  graph: dia.Graph | null,
): UseAutoGroupLightsReturn {
  const buildPreview = useCallback((): AutoGroupPreview => {
    if (!graph) {
      return { candidates: [], suggestedThreshold: 0, medianShortSide: 0 }
    }

    const candidates: AutoGroupCandidate[] = []
    const orphanShortSides: number[] = []

    graph.getElements().forEach((el) => {
      const data = el.get('data') as
        | { layer?: string; isGroup?: boolean; groupName?: string }
        | undefined
      if (!data) return

      if (data.isGroup) {
        // 기존 라이트 프레임만 흡수 대상으로 포함.
        const isLightGroup =
          data.groupName === 'l-light' ||
          data.groupName === 'l-lightingline' ||
          data.layer === 'l-light'
        if (!isLightGroup) return
        candidates.push({
          id: el.id as string,
          bbox: computeBbox(el),
          isExistingGroup: true,
        })
        return
      }

      if (!data.layer || !LIGHT_LAYERS.has(data.layer)) return
      const bbox = computeBbox(el)
      candidates.push({ id: el.id as string, bbox, isExistingGroup: false })
      orphanShortSides.push(Math.min(bbox.width, bbox.height))
    })

    if (candidates.length === 0) {
      return { candidates: [], suggestedThreshold: 0, medianShortSide: 0 }
    }

    // 임계값 추천: orphan 라이트의 짧은 변 중앙값 × 0.5.
    // 기존 그룹 bbox 는 여러 셀을 감싸므로 단위 거리로 부적합 → 제외.
    // orphan 이 하나도 없을 땐 전체 후보의 짧은 변 중앙값으로 fallback.
    const shortSides =
      orphanShortSides.length > 0
        ? orphanShortSides
        : candidates.map((c) => Math.min(c.bbox.width, c.bbox.height))
    const medianShortSide = median(shortSides)
    const suggestedThreshold = medianShortSide * 0.5

    return { candidates, suggestedThreshold, medianShortSide }
  }, [graph])

  const clusterByThreshold = useCallback(
    (candidates: AutoGroupCandidate[], threshold: number): string[][] =>
      clusterCandidates(candidates, threshold),
    [],
  )

  return { buildPreview, clusterByThreshold }
}

/**
 * AABB 거리 기반 클러스터링 (테스트 가능한 순수 함수).
 * 두 후보의 bbox 거리가 threshold 이하면 같은 클러스터.
 * 크기 ≥ 2 인 클러스터만 반환.
 */
export function clusterCandidates(
  candidates: AutoGroupCandidate[],
  threshold: number,
): string[][] {
  const n = candidates.length
  if (n < 2) return []

  const uf = new UnionFind(n)
  // O(n²). n 이 매우 크면 (수천) rbush 도입을 고려하나 일반 도면(~수백)에선 충분.
  for (let i = 0; i < n; i++) {
    const bi = candidates[i].bbox
    for (let j = i + 1; j < n; j++) {
      if (rectDistance(bi, candidates[j].bbox) <= threshold) {
        uf.union(i, j)
      }
    }
  }

  const byRoot = new Map<number, string[]>()
  for (let i = 0; i < n; i++) {
    const root = uf.find(i)
    let arr = byRoot.get(root)
    if (!arr) {
      arr = []
      byRoot.set(root, arr)
    }
    arr.push(candidates[i].id)
  }

  // 단독 조명은 그룹 의미 없으므로 제외.
  const clusters: string[][] = []
  for (const ids of byRoot.values()) {
    if (ids.length >= 2) clusters.push(ids)
  }
  return clusters
}
